import SEOMeta from '../../components/SEOMeta';
import {
  GENRE_EXAMPLES,
  PLANS,
  toOuvirExample,
  toWhatsAppExample,
} from '../../data/portfolio-examples';
import { WHATSAPP_PHONE } from '../../data/whatsapp';
import GenreVideoCard from './components/GenreVideoCard';
import OuvirPageCard from './components/OuvirPageCard';
import PlanHeader from './components/PlanHeader';
import PlanSummary from './components/PlanSummary';
import WhatsAppAudioCard from './components/WhatsAppAudioCard';
import { useExclusivePlayback } from './playback';
import './styles.css';

// Volta para a conversa da Isa sem texto pronto. `data-whatsapp-plain`
// impede o UTMTracker de injetar a mensagem padrão do site.
const WHATSAPP_RETURN_URL = `https://wa.me/${WHATSAPP_PHONE}`;

const planById = (id: string) => {
  const plan = PLANS.find((item) => item.id === id);
  if (!plan) throw new Error(`Plano "${id}" ausente em PLANS`);
  return plan;
};

function GenreChips() {
  const genreNames = Array.from(new Set(GENRE_EXAMPLES.map((example) => example.genre)));
  return (
    <>
      <p className="ex-genres__label">Os mesmos 6 ritmos em todas as opções:</p>
      <ul className="ex-genres">
        {genreNames.map((genre) => (
          <li key={genre}>{genre}</li>
        ))}
      </ul>
    </>
  );
}

export default function ExemplosPage() {
  const { registerMedia, claimPlayback } = useExclusivePlayback();
  const videoPlan = planById('video');
  const audioPlan = planById('audio');
  const paginaPlan = planById('pagina');

  return (
    <>
      <SEOMeta
        title="Exemplos"
        description="Exemplos de músicas personalizadas da Inspira, nos formatos em que elas são entregues."
        path="/exemplos"
        noIndex
      />

      <div className="ex-page">
        <header className="ex-header">
          <img
            className="ex-header__logo"
            src="/logo-light.png"
            alt="Inspira Músicas"
          />
        </header>

        <main className="ex-main">
          <h1 className="ex-title">Veja e ouça como a música fica</h1>
          <p className="ex-lead">
            Cada música é feita do zero, com a história da pessoa e no ritmo que ela gosta.
          </p>
          <p className="ex-lead ex-lead--strong">
            São 3 opções. O que muda entre elas é a forma como a música chega:
          </p>

          <PlanSummary plans={PLANS} />

          <section className="ex-section" id={audioPlan.anchor} aria-labelledby="audio-titulo">
            <PlanHeader plan={audioPlan} />

            <p className="ex-section__text">
              A música completa chega como uma mensagem de voz na conversa do WhatsApp. Dá para
              ouvir na hora, guardar no celular e reenviar para quem você quiser.
            </p>

            <GenreChips />

            <div className="ex-shelf">
              {GENRE_EXAMPLES.map((example) => (
                <WhatsAppAudioCard
                  key={example.id}
                  example={toWhatsAppExample(example)}
                  registerMedia={registerMedia}
                  claimPlayback={claimPlayback}
                />
              ))}
            </div>
          </section>

          <section className="ex-section" id={paginaPlan.anchor} aria-labelledby="pagina-titulo">
            <PlanHeader plan={paginaPlan} />

            <p className="ex-section__text">
              A música ganha uma página na internet, com a letra escrita na tela. A pessoa abre o
              link, escuta e acompanha cada verso enquanto a música toca.
            </p>

            <GenreChips />

            <div className="ex-shelf">
              {GENRE_EXAMPLES.map((example) => (
                <OuvirPageCard
                  key={example.id}
                  example={toOuvirExample(example)}
                  registerMedia={registerMedia}
                  claimPlayback={claimPlayback}
                />
              ))}
            </div>
          </section>

          <section className="ex-section" id={videoPlan.anchor} aria-labelledby="video-titulo">
            <PlanHeader plan={videoPlan} />

            <p className="ex-section__text">
              Cada vídeo aqui embaixo tem a música completa, do começo ao fim, com as fotos da
              pessoa aparecendo. O ritmo é escolhido por quem vai ganhar, e isso vale para as três
              opções.
            </p>

            <GenreChips />

            <div className="ex-shelf">
              {GENRE_EXAMPLES.map((example) => (
                <GenreVideoCard
                  key={example.id}
                  example={example}
                  price={videoPlan.price}
                  registerMedia={registerMedia}
                  claimPlayback={claimPlayback}
                />
              ))}
            </div>
          </section>

          <div className="ex-close">
            <p>Ainda em dúvida sobre qual escolher?</p>
            <p className="ex-close__strong">
              Volte para o WhatsApp e pergunte. A gente te ajuda a decidir.
            </p>
          </div>
        </main>

        <div className="ex-ctabar">
          <div className="ex-ctabar__inner">
            <a
              className="ex-ctabar__link"
              href={WHATSAPP_RETURN_URL}
              data-whatsapp-plain=""
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Voltar ao WhatsApp
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
